import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import { mapVehicleEventIconWrapped } from "../../../assets/svg/vehicle-event-wrapped";
import { mapVehicleIconWrapped } from "../../../assets/svg/vehicle-wrapped";

export const MOTION_STATUS_CODES = new Set<VehicleStatusCode>([
  VehicleStatusCode.STATUS_MOTION_HEADING,
  VehicleStatusCode.STATUS_MOTION_MOVING,
  VehicleStatusCode.STATUS_MOTION_ENROUTE,
  VehicleStatusCode.STATUS_DISTANCE,
  VehicleStatusCode.STATUS_DISTANCE_1,
  VehicleStatusCode.STATUS_DISTANCE_2,
]);


const HEADING_BUCKET_DEGREES = 5;

const iconUrlCache = new Map<string, string>();

// `size` undefined means "whatever the icon builders default to" - the
// route renderers rely on that default, so it has to stay distinct from an
// explicit size in the cache key.
const cachedIconUrl = (
  point: VehicleMapDataPoint,
  size?: number
): string => {
  const isMotion = MOTION_STATUS_CODES.has(point.statuscode);
  const headingBucket = isMotion
    ? Math.round((point.heading || 0) / HEADING_BUCKET_DEGREES) *
      HEADING_BUCKET_DEGREES
    : 0;
  const cacheKey = `${point.statuscode}|${size ?? "default"}|${headingBucket}`;

  const cached = iconUrlCache.get(cacheKey);
  if (cached) return cached;

  const svg = isMotion
    ? size === undefined
      ? mapVehicleIconWrapped("driving", false, headingBucket)
      : mapVehicleIconWrapped("driving", false, headingBucket, size)
    : size === undefined
      ? mapVehicleEventIconWrapped(point.statuscode, false)
      : mapVehicleEventIconWrapped(point.statuscode, false, size);
  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  iconUrlCache.set(cacheKey, url);
  return url;
};

// the same icon set the rest of the app uses for trip events
export const eventIconUrl = (
  point: VehicleMapDataPoint,
  size: number = 24
): string => cachedIconUrl(point, size);

// How a pin is being singled out. Where events stack on top of each other -
// which on a real trip is most of them - size is the only thing that says
// which pin the pointer is actually on, and which one was picked.
export type EventPinFocus = "none" | "hover" | "selected";

const BASE_PIN_SIZE = 28;
const PIN_SIZE_BY_FOCUS: Record<EventPinFocus, number> = {
  none: BASE_PIN_SIZE,
  hover: 36,
  selected: 44,
};

// Stacking order follows the same ranking, or a grown pin can still end up
// drawn under the ordinary ones it is sitting among. The selected value is
// far clear of the others on purpose: picking one event out of ten at the
// same position only reads if that one comes to the front of the pile.
export const PIN_Z_INDEX_BY_FOCUS: Record<EventPinFocus, number> = {
  none: 120,
  hover: 170,
  selected: 400,
};

// Shared, immutable geometry for the map renderers' pins, one entry per
// size. google.maps.Size and Point are value objects, so one instance can
// back every marker instead of two allocations per pin.
const pinGeometryBySize = new Map<
  number,
  { scaledSize: google.maps.Size; anchor: google.maps.Point }
>();

const pinGeometryFor = (size: number) => {
  const cached = pinGeometryBySize.get(size);
  if (cached) return cached;
  const geometry = {
    scaledSize: new google.maps.Size(size, size),
    // the base anchor is (14, 24) on a 28px pin - the tip of the teardrop,
    // not its centre - so it has to scale with the icon, or a grown pin
    // would drift off the fix it is marking
    anchor: new google.maps.Point(size / 2, (size * 24) / BASE_PIN_SIZE),
  };
  pinGeometryBySize.set(size, geometry);
  return geometry;
};

// the full pin shape (white marker background) the route renderers drop on
// a focused trip - the bare icon used in inline list rows is eventIconUrl
export const eventMarkerIcon = (
  point: VehicleMapDataPoint,
  focus: EventPinFocus = "none"
) => {
  const size = PIN_SIZE_BY_FOCUS[focus];
  const geometry = pinGeometryFor(size);
  return {
    // the base size is what the icon builders default to - asking for it by
    // number would cache the identical artwork under a second key
    url: cachedIconUrl(point, focus === "none" ? undefined : size),
    scaledSize: geometry.scaledSize,
    anchor: geometry.anchor,
  };
};
